import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { IconSettings } from "@tabler/icons-react";
import {
  Switch,
  Typography,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormDescription,
  FormLabel,
  Button,
  Separator,
  RadioGroup,
  RadioGroupItem,
  Input,
  Alert,
  AlertTitle,
  AlertDescription,
  Tooltip,
  toast,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import {
  getWarehouseAutoTuneConfiguration,
  setWarehouseAutoTuneConfiguration,
} from "@/helpers/apis";
import {
  GET_AUTO_TUNE_CONFIGURATION,
  SET_AUTO_TUNE_CONFIGURATION,
  defaultAutoTuneConfig,
  autoTuneConfigurationFormSchema,
} from "../../constants";
import { LoadingSpinner, ExclamationCircle, InfoIcon } from "@/assets/icons";
import {
  WarehouseAutoTuneConfiguration,
  SetWarehouseAutoTuneConfigurationRequest,
} from "../../types";
import { captureException } from "@sentry/react";
import { WarehouseTracking } from "@/lib/tracking/warehouse";
import { isOwner, isAdmin } from "@/services/auth";

interface WarehouseAutoTuneControlProps {
  warehouseRK: string;
  warehouseName: string;
  defaultWarehouseSize?: string;
  autoTuneConfiguration: WarehouseAutoTuneConfiguration;
}

export function WarehouseAutoTuneControl({
  warehouseRK,
  warehouseName,
  defaultWarehouseSize,
  autoTuneConfiguration: autoTuneConfigurationProp,
}: WarehouseAutoTuneControlProps) {
  const { instance_id } = useParams();
  const queryClient = useQueryClient();
  const canToggleAutoTune = isOwner() || isAdmin();
  const [autoTuneConfiguration, setAutoTuneConfiguration] =
    useState<WarehouseAutoTuneConfiguration>(defaultAutoTuneConfig);
  const [autoTuneConfigurationOpen, setAutoTuneConfigurationOpen] = useState(false);
  const [autoTuneSwitchEnabled, setAutoTuneSwitchEnabled] = useState(false);
  const [dynamicResizingEligible, setDynamicResizingEligible] = useState(true);
  const [autoTuneConfigurationError, setAutoTuneConfigurationError] = useState("");

  const autoTuneConfigurationForm = useForm({
    resolver: zodResolver(autoTuneConfigurationFormSchema),
    mode: "onChange",
    defaultValues: {
      auto_tune_enabled: false,
      resizing_enabled: false,
      resizing_mode: "manual_resize" as "manual_resize" | "auto_resize" | "disabled",
      max_latency_percentage: 150,
      max_queue_time_percentage: 150,
      auto_approve_resizing: false,
    },
  });

  const resizingEnabled = autoTuneConfigurationForm.watch("resizing_enabled");
  const resizingMode = autoTuneConfigurationForm.watch("resizing_mode");

  // Ensure we have a valid value when resizing is enabled
  useEffect(() => {
    if (resizingEnabled && (!resizingMode || resizingMode === "disabled")) {
      autoTuneConfigurationForm.setValue("resizing_mode", "manual_resize");
    }
  }, [resizingEnabled, resizingMode, autoTuneConfigurationForm]);

  const { data: autoTuneConfigData, refetch: autoTuneConfigRefetch } = useQuery({
    queryKey: [GET_AUTO_TUNE_CONFIGURATION, instance_id, warehouseRK],
    queryFn: () => getWarehouseAutoTuneConfiguration(instance_id!, warehouseRK),
    enabled: false,
  });

  const setAutotuneConfigurationFormValues = useCallback(
    (data: WarehouseAutoTuneConfiguration) => {
      autoTuneConfigurationForm.setValue(
        "auto_tune_enabled",
        data.auto_tune_configuration.auto_tune_enabled
      );
      autoTuneConfigurationForm.setValue(
        "resizing_enabled",
        data.resizing_configuration.resizing_enabled
      );
      autoTuneConfigurationForm.setValue(
        "resizing_mode",
        data.resizing_configuration.resizing_configuration?.mode ??
          defaultAutoTuneConfig.resizing_configuration.resizing_configuration.mode
      );
      autoTuneConfigurationForm.setValue(
        "max_latency_percentage",
        data.resizing_configuration.resizing_configuration?.mode === "auto_resize"
          ? (data.resizing_configuration.resizing_configuration.backoff_configuration
              ?.max_latency_percentage ??
              defaultAutoTuneConfig.resizing_configuration.resizing_configuration
                .backoff_configuration.max_latency_percentage)
          : defaultAutoTuneConfig.resizing_configuration.resizing_configuration
              .backoff_configuration.max_latency_percentage
      );
      autoTuneConfigurationForm.setValue(
        "max_queue_time_percentage",
        data.resizing_configuration.resizing_configuration?.mode === "auto_resize"
          ? (data.resizing_configuration.resizing_configuration.backoff_configuration
              ?.max_queue_time_percentage ??
              defaultAutoTuneConfig.resizing_configuration.resizing_configuration
                .backoff_configuration.max_queue_time_percentage)
          : defaultAutoTuneConfig.resizing_configuration.resizing_configuration
              .backoff_configuration.max_queue_time_percentage
      );
    },
    [autoTuneConfigurationForm]
  );

  // Handle successful auto tune config fetch (replaces onSuccess callback removed in TanStack Query v5)
  // Also handles config passed via prop
  useEffect(() => {
    const configData = autoTuneConfigData || autoTuneConfigurationProp;
    if (configData) {
      setAutoTuneConfiguration(configData);
      setAutotuneConfigurationFormValues(configData);
    }
  }, [autoTuneConfigData, autoTuneConfigurationProp, setAutotuneConfigurationFormValues]);

  useEffect(() => {
    setAutoTuneSwitchEnabled(
      autoTuneConfiguration.auto_tune_configuration.auto_tune_enabled ||
        autoTuneConfiguration.resizing_configuration.resizing_enabled
    );
    setDynamicResizingEligible(
      autoTuneConfiguration.dynamic_resizing_eligible !== null &&
        autoTuneConfiguration.dynamic_resizing_eligible !== false
    );
  }, [autoTuneConfiguration]);

  const { mutate: autoTuneConfigMutate, isPending: autoTuneConfigMutateLoading } = useMutation({
    mutationFn: (variables: {
      instance: string;
      warehouse: string;
      config: SetWarehouseAutoTuneConfigurationRequest;
    }) => {
      return setWarehouseAutoTuneConfiguration(
        variables.instance,
        variables.warehouse,
        variables.config
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SET_AUTO_TUNE_CONFIGURATION, instance_id, warehouseRK],
      });
      setAutoTuneConfigurationOpen(false);
      autoTuneConfigRefetch();
    },
    onError: (error: Error) => {
      setAutoTuneConfigurationError(
        error?.message || (error as unknown as string) || "Error updating configuration"
      );
      setTimeout(() => {
        setAutoTuneConfigurationError("");
      }, 10000);
    },
  });

  const handleAutoTuneChange = (checked: boolean) => {
    if (!instance_id) {
      captureException(new Error("Instance ID is missing while updating auto tune"));
      toast({
        variant: "destructive",
        description: "Something went wrong!. Please try again later",
      });
      return;
    }
    WarehouseTracking.autoTuneToggled(warehouseName, checked);
    if (checked) {
      setAutoTuneConfigurationOpen(true);
    } else {
      setAutoTuneConfigurationOpen(false);
      setAutoTuneConfiguration(defaultAutoTuneConfig);
      setAutotuneConfigurationFormValues(defaultAutoTuneConfig);
      autoTuneConfigMutate({
        instance: instance_id,
        warehouse: warehouseRK,
        config: defaultAutoTuneConfig,
      });
    }
  };

  const handleAutoTuneConfigurationCancel = () => {
    setAutotuneConfigurationFormValues(autoTuneConfiguration);
    setAutoTuneConfigurationOpen(false);
  };

  function onSubmit(
    autoTuneConfigurationFormValues: z.infer<typeof autoTuneConfigurationFormSchema>
  ) {
    if (!instance_id) {
      captureException(new Error("Instance ID is missing while submitting auto tune"));
      toast({
        variant: "destructive",
        description: "Something went wrong!. Please try again later",
      });
      return;
    }
    WarehouseTracking.autoTuneConfigured(
      warehouseName,
      autoTuneConfigurationFormValues.resizing_mode,
      autoTuneConfigurationFormValues.resizing_enabled
    );
    autoTuneConfigMutate({
      instance: instance_id,
      warehouse: warehouseRK,
      config: {
        auto_tune_configuration: {
          auto_tune_enabled: autoTuneConfigurationFormValues.auto_tune_enabled,
        },
        resizing_configuration: {
          resizing_enabled: autoTuneConfigurationFormValues.resizing_enabled,
          resizing_configuration: {
            mode: autoTuneConfigurationFormValues.resizing_mode,
            backoff_configuration: {
              max_latency_percentage: autoTuneConfigurationFormValues.max_latency_percentage,
              max_queue_time_percentage: autoTuneConfigurationFormValues.max_queue_time_percentage,
            },
            auto_approve_resizing: false,
          },
        },
        dynamic_resizing_eligible: null,
        dynamic_resizing_eligible_reason: null,
      },
    });
  }

  return (
    <>
      <Stack direction="row" align="center" gap="md">
        <Tooltip
          title={
            !canToggleAutoTune
              ? "You don't have permission to toggle Auto Tune"
              : `Auto Tune is ${autoTuneSwitchEnabled ? "enabled" : "disabled"}`
          }
        >
          <Stack
            direction="row"
            align="center"
            gap="md"
            data-autotune-enabled={autoTuneSwitchEnabled}
            className={cn(
              "al-ps-2 al-rounded-md al-border al-min-h-9",
              !canToggleAutoTune && "al-opacity-60 al-cursor-not-allowed",
              "data-[autotune-enabled=true]:al-border-stroke-brand-light data-[autotune-enabled=true]:al-bg-bg-brand data-[autotune-enabled=true]:al-text-brand",
              "data-[autotune-enabled=false]:al-bg-bg-gray data-[autotune-enabled=false]:al-text-gray"
            )}
          >
            <Switch
              checked={autoTuneSwitchEnabled}
              onCheckedChange={handleAutoTuneChange}
              disabled={!canToggleAutoTune}
              className="data-[state=checked]:!al-bg-[#004FBF]"
              aria-label="Auto tune toggle switch"
            />
            <Typography variant="body" size="sm" weight="medium">
              Auto Tune
            </Typography>
            {autoTuneSwitchEnabled && (
              <div className={cn("al-border-l", "al-border-stroke-brand-light")}>
                <Tooltip title="Auto Tune Settings">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setAutoTuneConfigurationOpen(true)}
                    title="Auto tune settings button"
                    className="hover:!al-bg-transparent"
                  >
                    <IconSettings className="!al-size-5" />
                  </Button>
                </Tooltip>
              </div>
            )}
          </Stack>
        </Tooltip>
      </Stack>

      <Sheet open={autoTuneConfigurationOpen} onOpenChange={setAutoTuneConfigurationOpen}>
        <SheetContent className="!al-w-1/2 !al-max-w-[700px] al-z-[100]">
          <SheetHeader>
            <SheetTitle>Auto Tune Settings</SheetTitle>
            <SheetDescription>
              <Stack wrap>
                <Typography variant="body" size="xs" className="al-text-muted-foreground">
                  Warehouse Name: {warehouseName}
                </Typography>
                {defaultWarehouseSize && (
                  <Typography variant="body" size="xs" className="al-text-muted-foreground">
                    Default Size: {defaultWarehouseSize}
                  </Typography>
                )}
              </Stack>
            </SheetDescription>
          </SheetHeader>
          <Form {...autoTuneConfigurationForm}>
            <form
              aria-label="Auto tune configuration form"
              className="al-mt-4"
              onSubmit={autoTuneConfigurationForm.handleSubmit(onSubmit)}
              onReset={() => autoTuneConfigurationForm.reset()}
            >
              <Stack className="al-mt-3" direction="column" gap="md">
                <Stack direction="column" gap="md">
                  <FormField
                    control={autoTuneConfigurationForm.control}
                    name="auto_tune_enabled"
                    render={({ field }) => (
                      <FormItem className="al-rounded-lg al-border al-p-3">
                        <Stack direction="row" align="center" justify="between">
                          <div className="al-space-y-0.5">
                            <FormLabel>Warehouse Shutdown & Scaling</FormLabel>
                            <FormDescription>
                              Automatically suspend and scale warehouses based on usage patterns
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              aria-label="Auto tune toggle switch"
                            />
                          </FormControl>
                        </Stack>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Separator className="al-my-2.5" />
                  {dynamicResizingEligible && (
                    <div className="al-space-y-4 al-border al-p-3 al-rounded-lg">
                      <FormField
                        control={autoTuneConfigurationForm.control}
                        name="resizing_enabled"
                        render={({ field }) => (
                          <div className="al-space-y-4">
                            <FormItem>
                              <Stack direction="row" align="center" justify="between">
                                <div className="al-space-y-0.5">
                                  <FormLabel>Warehouse Sizing</FormLabel>
                                  <FormDescription>
                                    Automatically resize warehouses based on workload demand
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    disabled={!dynamicResizingEligible}
                                    aria-label="Warehouse sizing toggle switch"
                                  />
                                </FormControl>
                              </Stack>
                              <FormMessage />
                            </FormItem>
                          </div>
                        )}
                      />
                      <FormField
                        control={autoTuneConfigurationForm.control}
                        name="resizing_mode"
                        render={({ field }) => {
                          return (
                            <FormItem>
                              <FormControl>
                                <RadioGroup
                                  className="!al-gap-2 !al-w-full"
                                  orientation="horizontal"
                                  onValueChange={field.onChange}
                                  value={
                                    resizingEnabled ? field.value || "manual_resize" : undefined
                                  }
                                  disabled={!dynamicResizingEligible || !resizingEnabled}
                                >
                                  <FormItem className="al-w-full">
                                    <Stack
                                      className={cn(
                                        "p-3 bg-white al-rounded-md al-border al-w-full",
                                        (!dynamicResizingEligible || !resizingEnabled) &&
                                          "al-opacity-75 al-cursor-not-allowed al-bg-gray-50"
                                      )}
                                      justify="start"
                                      align="start"
                                    >
                                      <FormControl>
                                        <RadioGroupItem
                                          className="al-mt-1"
                                          value="manual_resize"
                                          aria-label="Approval mode"
                                        />
                                      </FormControl>
                                      <Stack direction="column" gap="sm">
                                        <span
                                          className={cn(
                                            (!dynamicResizingEligible || !resizingEnabled) &&
                                              "al-text-gray-400"
                                          )}
                                        >
                                          Approval
                                        </span>
                                        <span
                                          className={cn(
                                            "al-text-xs",
                                            (!dynamicResizingEligible || !resizingEnabled) &&
                                              "al-text-gray-400"
                                          )}
                                        >
                                          Runs on a fixed time schedule
                                        </span>
                                      </Stack>
                                    </Stack>
                                  </FormItem>
                                  <FormItem className="al-w-full">
                                    <Stack
                                      className={cn(
                                        "p-3 bg-white al-rounded-md al-border al-w-full",
                                        (!dynamicResizingEligible || !resizingEnabled) &&
                                          "al-opacity-75 al-cursor-not-allowed al-bg-gray-50"
                                      )}
                                      justify="start"
                                      align="start"
                                    >
                                      <FormControl>
                                        <RadioGroupItem
                                          className="al-mt-1"
                                          value="auto_resize"
                                          aria-label="Real-Time mode"
                                        />
                                      </FormControl>
                                      <Stack direction="column" gap="sm">
                                        <span
                                          className={cn(
                                            (!dynamicResizingEligible || !resizingEnabled) &&
                                              "al-text-gray-400"
                                          )}
                                        >
                                          Real-Time
                                        </span>
                                        <span
                                          className={cn(
                                            "al-text-xs",
                                            (!dynamicResizingEligible || !resizingEnabled) &&
                                              "al-text-gray-400"
                                          )}
                                        >
                                          Automatically adjusts based on usage
                                        </span>
                                      </Stack>
                                    </Stack>
                                  </FormItem>
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    </div>
                  )}
                  {!dynamicResizingEligible && (
                    <Alert variant="warning">
                      <ExclamationCircle className="al-size-4" />
                      <AlertTitle className="al-text-yellow-800 !al-text-base !al-leading-4">
                        Auto-resizing not enabled for warehouse {warehouseName}
                      </AlertTitle>
                      <AlertDescription>
                        This warehouse is not eligible for auto-resizing modes because it lacks
                        predictable workload patterns, has low utilization, and does not present
                        significant cost-saving opportunities. You can still create and manage
                        warehouse schedules using the calendar.
                      </AlertDescription>
                    </Alert>
                  )}
                </Stack>
                {resizingEnabled && resizingMode === "auto_resize" && (
                  <Stack
                    direction="column"
                    gap="lg"
                    className="al-p-3 al-w-full al-bg-[#E9F2FF] al-text-black al-rounded-md"
                  >
                    <Stack direction="column" gap="md">
                      <span className="al-text-base al-font-semibold">
                        Real-Time Backoffs Configuration
                      </span>
                      <span className="al-text-sm al-font-normal al-text-light-gray">
                        Back off whenever the metric exceeds the specified threshold percentage of
                        the P99 value recorded on the same day last week.
                      </span>
                    </Stack>
                    <Stack direction="column" gap="md">
                      <span className="al-text-base al-font-semibold">Metric</span>
                      <Stack justify="between" align="center" className="al-w-full">
                        <Stack direction="column" gap="xxs" className="al-w-4/5">
                          <span className="al-text-xs al-font-semibold">Query</span>
                          <span className="al-text-xs">Based on current queue time</span>
                          <span className="al-text-xs al-text-brand">
                            Queries over the last 30 mins
                          </span>
                        </Stack>
                        <FormField
                          control={autoTuneConfigurationForm.control}
                          name="max_queue_time_percentage"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="number"
                                  className="al-w-1/5"
                                  {...field}
                                  aria-label="Max queue time percentage"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </Stack>
                      <Stack justify="between" align="center" className="al-w-full">
                        <Stack direction="column" gap="xxs" className="al-w-4/5">
                          <span className="al-text-xs al-font-semibold">Latency</span>
                          <span className="al-text-xs">Based on current query latency</span>
                          <span className="al-text-xs al-text-brand">
                            Queries over the last 30 mins
                          </span>
                        </Stack>
                        <FormField
                          control={autoTuneConfigurationForm.control}
                          name="max_latency_percentage"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="number"
                                  className="al-w-1/5"
                                  {...field}
                                  aria-label="Max latency percentage"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
                <Stack direction="row" align="center" justify="between">
                  <Button variant="outline" type="reset">
                    Reset to Default
                  </Button>
                  <Stack direction="row" align="center" justify="start">
                    <Button variant="outline" onClick={handleAutoTuneConfigurationCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      type="submit"
                      disabled={
                        autoTuneConfigMutateLoading || !autoTuneConfigurationForm.formState.isValid
                      }
                    >
                      {!autoTuneConfigMutateLoading && "Save Changes"}
                      {autoTuneConfigMutateLoading && <LoadingSpinner />}
                    </Button>
                  </Stack>
                </Stack>
                {autoTuneConfigurationError.length > 0 && (
                  <Alert variant="destructive">
                    <InfoIcon className="al-h-4 al-w-4 !al-text-destructive" />
                    <AlertTitle className="al-text-base al-text-destructive">Error</AlertTitle>
                    <AlertDescription>{autoTuneConfigurationError}</AlertDescription>
                  </Alert>
                )}
              </Stack>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
}
